import { forwardRef } from 'react'
import {
  DatePicker,
  Input,
  Select,
  Textarea,
  Toast,
  Typography
} from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'

import { Container, Item } from '../Claims.styles'
import ClaimsAttachedFiles from './ClaimsAttachedFiles'
import { useClaimsForm } from './ClaimsForm.hook'

const requestSliceShape = PropTypes.shape({
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  error: PropTypes.object,
  loading: PropTypes.bool
})

/**
 * The ClaimsForm component.
 */
const ClaimsForm = forwardRef((props, ref) => {
  const {
    attachedFiles = [],
    claimReasons,
    claimTypes,
    onAddFile,
    onRemoveFile,
    onSearchOrganizationByDni,
    onSearchOrganizationByProcessCode,
    organization,
    submitLoading
  } = props

  const {
    acceptanceDate,
    claimReason,
    claimReasonErrorText,
    claimType,
    claimTypeErrorText,
    dni,
    invoiceAmount,
    invoiceDate,
    invoiceNumber,
    isLatePayment,
    isProcurementIrregularity,
    isReasonTextareaDisabled,
    orgLegalName,
    orgLegalNameErrorText,
    processCode,
    reason,
    REASON_MAX_LENGTH
  } = useClaimsForm({ ref, ...props })

  const { error: claimReasonsError, loading: claimReasonsLoading } = claimReasons
  const { data: claimTypesData, error: claimTypesError, loading: claimTypesLoading } = claimTypes
  const {
    disabledIfFound: disabledIfFoundOrganization,
    loading: organizationLoading,
    loadingOrgName
  } = organization

  return (
    <Container direction="column" spacing={2}>
      <Typography fontWeight="bold" variant="h4">
        Detalle del reclamo
      </Typography>

      <Toast margin="0 0 32px" severity="info">
        Como ChileCompra somos el canal de ingreso de la solicitud, sin embargo la respuesta final
        debe ser entregada por el organismo público reclamado. La entidad dispondrá de un máximo de
        5 días hábiles para responder contados desde el ingreso del reclamo.
      </Toast>

      <Item size={{ md: 6, xs: 12 }}>
        <Select
          disabled={
            Boolean(claimTypesError) || claimReasonsLoading || organizationLoading || submitLoading
          }
          error={claimTypeErrorText}
          label="Tipo de reclamo"
          loading={claimTypesLoading}
          onChange={claimType.onChange}
          options={claimTypesData}
          placeholder={
            claimTypesError
              ? 'Sin tipos de reclamo disponibles'
              : 'Seleccione un tipo de reclamo...'
          }
          value={claimType.value}
        />
      </Item>

      <Typography variant="body1">
        Nosotros, como Dirección ChileCompra, derivamos tu reclamo y notificaremos al organismo
        público relacionado.
      </Typography>

      <Container spacing={2}>
        <Item size={{ md: 6, xs: 12 }}>
          <Input
            disabled={
              !claimType.value || claimReasonsLoading || organizationLoading || submitLoading
            }
            error={processCode.error}
            label="ID del proceso"
            loading={claimTypesLoading}
            onBlur={() => onSearchOrganizationByProcessCode(processCode.value)}
            onChange={processCode.onChange}
            placeholder="Ingresa el ID del proceso"
            value={processCode.value}
          />
        </Item>

        <Item size={{ sm: 6, xs: 12 }}>
          <Input
            disabled={disabledIfFoundOrganization}
            error={dni.error}
            label="RUT organismo público"
            loading={claimTypesLoading || organizationLoading}
            onBlur={() => onSearchOrganizationByDni(dni.value)}
            onChange={dni.onChange}
            placeholder="RUT del organismo público"
            value={dni.value}
          />
        </Item>

        <Item size={{ sm: 6, xs: 12 }}>
          <Input
            disabled
            error={orgLegalNameErrorText}
            label="Organismo público"
            loading={claimTypesLoading || organizationLoading || loadingOrgName}
            onChange={orgLegalName.onChange}
            placeholder="Nombre del organismo público"
            value={orgLegalName.value}
          />
        </Item>
      </Container>

      {isProcurementIrregularity && (
        <Item size={{ md: 6, xs: 12 }}>
          <Select
            disabled={Boolean(claimReasonsError) || organizationLoading || submitLoading}
            error={claimReasonErrorText}
            label="¿Cuál es el motivo de tu reclamo?"
            loading={claimTypesLoading || claimReasonsLoading}
            onChange={claimReason.onChange}
            options={claimReasons.data}
            placeholder={claimReasonsError ? 'Sin motivos disponibles' : 'Seleccione un motivo...'}
            value={claimReason.value}
          />
        </Item>
      )}

      {isLatePayment && (
        <>
          <Container spacing={2}>
            <Item size={{ sm: 6, xs: 12 }}>
              <DatePicker
                disabled={organizationLoading || submitLoading}
                error={acceptanceDate.error}
                format="dd/MM/yyyy"
                label="Fecha de recepción conforme"
                loading={claimTypesLoading}
                localeText={{
                  fieldYearPlaceholder: params => 'A'.repeat(params.digitAmount)
                }}
                maxDate={new Date()}
                onChange={acceptanceDate.onChange}
                value={acceptanceDate.value}
              />
            </Item>

            <Item size={{ sm: 6, xs: 12 }}>
              <DatePicker
                disabled={organizationLoading || submitLoading}
                error={invoiceDate.error}
                format="dd/MM/yyyy"
                label="Fecha de entrega de la factura"
                loading={claimTypesLoading}
                localeText={{
                  fieldYearPlaceholder: params => 'A'.repeat(params.digitAmount)
                }}
                maxDate={new Date()}
                onChange={invoiceDate.onChange}
                value={invoiceDate.value}
              />
            </Item>
          </Container>

          <Container spacing={2}>
            <Item size={{ sm: 6, xs: 12 }}>
              <Input
                disabled={organizationLoading || submitLoading}
                error={invoiceNumber.error}
                label="Número de la factura"
                loading={claimTypesLoading}
                maxLength={40}
                onChange={invoiceNumber.onChange}
                placeholder="Ingresa el número de la factura"
                value={invoiceNumber.value}
              />
            </Item>

            <Item size={{ sm: 6, xs: 12 }}>
              <Input
                disabled={organizationLoading || submitLoading}
                error={invoiceAmount.error}
                label="Monto de la factura"
                loading={claimTypesLoading}
                maxLength={20}
                onChange={invoiceAmount.onChange}
                placeholder="Ingresa el monto de la factura"
                value={invoiceAmount.value}
              />
            </Item>
          </Container>
        </>
      )}

      <Item size={12}>
        <Textarea
          disabled={isReasonTextareaDisabled || submitLoading}
          error={reason.error}
          label="Motivo"
          loading={claimTypesLoading}
          maxCount={REASON_MAX_LENGTH}
          maxLength={REASON_MAX_LENGTH}
          minRows={8}
          onChange={reason.onChange}
          value={reason.value}
        />
      </Item>

      <Item size={12}>
        <ClaimsAttachedFiles
          attachedFiles={attachedFiles}
          isDisabled={
            Boolean(claimTypesError) ||
            claimTypesLoading ||
            claimReasonsLoading ||
            organizationLoading ||
            submitLoading
          }
          onAdd={onAddFile}
          onRemove={onRemoveFile}
        />
      </Item>
    </Container>
  )
})

ClaimsForm.displayName = 'ClaimsForm'

ClaimsForm.propTypes = {
  attachedFiles: PropTypes.array,
  claimReasons: requestSliceShape.isRequired,
  claimTypes: requestSliceShape.isRequired,
  onAddFile: PropTypes.func.isRequired,
  onClaimTypeChange: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onResetFormErrors: PropTypes.func.isRequired,
  onResetOrganization: PropTypes.func.isRequired,
  onResetOrganizationOrgName: PropTypes.func.isRequired,
  onSearchOrganizationByDni: PropTypes.func.isRequired,
  onSearchOrganizationByProcessCode: PropTypes.func.isRequired,
  organization: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    disabledIfFound: PropTypes.bool,
    error: PropTypes.object,
    loading: PropTypes.bool,
    loadingOrgName: PropTypes.bool
  }).isRequired,
  submitError: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired
}

export default ClaimsForm
